"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
const formatDate = (dateString?: string) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};
function LatestArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="max-h-[800px] overflow-y-auto pr-2">
        <div data-section-id="13593" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 rounded-sm border border-border bg-card p-0 h-full overflow-hidden">
              <div className="w-full aspect-video bg-muted animate-pulse" />
              <div className="p-6 space-y-4">
                <div className="h-4 w-24 bg-muted animate-pulse rounded-sm" />
                <div className="h-6 w-3/4 bg-muted animate-pulse rounded-sm" />
                <div className="space-y-2">
                  <div className="h-4 w-full bg-muted animate-pulse rounded-sm" />
                  <div className="h-4 w-2/3 bg-muted animate-pulse rounded-sm" />
                </div>
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Unable to retrieve latest legal insights.</p>
        <Button onClick={refetch} variant="outline" className="rounded-sm border-primary text-primary hover:bg-primary/5">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 bg-muted/30 rounded-sm border border-dashed border-border">
        <p className="text-muted-foreground">No recent publications found.</p>
      </div>;
  }
  return <div className="max-h-[800px] overflow-y-auto pr-2">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => <Link key={post.id} data-index={i} to={post.link || "#"} className="group block h-full">
            <Card className="h-full border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl rounded-sm overflow-hidden flex flex-col">
              <div className="overflow-hidden">
                <AspectRatio ratio={16 / 9}>
                  <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605323/img/blog_placeholder_generic.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </AspectRatio>
              </div>

              <CardHeader className="p-6 pb-2 space-y-3">
                <div className="flex items-center justify-between">
                  {post.categories && post.categories.length > 0 && <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 font-semibold tracking-wider text-[10px] uppercase px-3 py-1 rounded-sm">
                      {post.categories[0].name}
                    </Badge>}
                  <div className="flex items-center text-xs text-muted-foreground font-sans">
                    <SafeHydrate><Calendar className="mr-1 h-3 w-3 text-primary" /></SafeHydrate>
                    {formatDate(post.date)}
                  </div>
                </div>
                <h3 className="text-2xl font-serif font-bold leading-tight text-foreground group-hover:text-primary transition-colors line-clamp-2">
                  {post.title}
                </h3>
              </CardHeader>

              <CardContent className="p-6 pt-2 flex-grow">
                <div className="text-muted-foreground text-sm line-clamp-3 leading-relaxed font-sans" dangerouslySetInnerHTML={{
              __html: post.excerpt || ""
            }} />
              </CardContent>

              <CardFooter className="p-6 pt-0 mt-auto border-t-0">
                <div className="flex items-center text-primary font-bold text-sm tracking-tight group/btn">
                  Read Full Brief
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                </div>
              </CardFooter>
            </Card>
          </Link>)}
      </div>
    </div>;
}
export default function LatestArticles() {
  const wp_query_params = {
    per_page: 100,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="articles-news-grid" className="py-24 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-foreground mb-6">Reports &amp; Articles</h2>
            <p className="text-xl text-muted-foreground font-sans leading-relaxed">AW Editor Note: We're in the process of bringing over our full archive from our previous site. If there's a specific report or article you're looking for, email us at articles@assessmentwatch.com — we'll get it to you.</p>
          </div>
          <Link>
            
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 100,
        orderby: "date",
        order: "desc"
      }}>
          <LatestArticlesConsumer />
        </PostsProvider>

        <div className="mt-12 text-center md:hidden">
          <Link>
            <Button variant="outline" className="w-full rounded-sm border-primary text-primary py-6 font-bold uppercase tracking-widest text-xs">
              View All Publications
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}